import { useState, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { saveAs } from 'file-saver';
import {
  color,
  Field2 as Field,
  rotationFullLabel,
  TimeUtil,
  VibeIcon,
  viCached,
  viChevronRight,
  viClose,
  viDownload,
  viLoopVideo,
  viPause,
  viPlayCircleDisplay,
  viPlayCircleOutline,
} from 'vibeguide';
import {
  Slider,
} from '@mui/material';
import {
  isEmpty,
  isFinite,
  kebabCase,
  sortBy,
  toNumber,
  uniq,
} from 'lodash';
import Carousel from 'react-multi-carousel';
import {
  setDisplay,
} from '../../actions/Display/DisplayActions';
import 'react-multi-carousel/lib/styles.css';
import VideoPlayer from './VideoPlayer';
import CustomLeftArrow from './CustomLeftArrow';
import CustomRightArrow from './CustomRightArrow';
import './DisplayPlayer.scss';

function DisplayPlayer({
  // mediaItems,
  messages,
  isLoop,
  resolution: propsResolution,
  rotation: propsRotation,
  setDisplay,
}) {
  const elapsedTimeRef = useRef(null);
  const playerRef = useRef(null);
  const progressBarRef = useRef(null);
  const rectTracXRef = useRef(null);
  const currentItemTimeoutRef = useRef(null);
  const currentItemIntervalRef = useRef(null);
  const [currentMediaItem, setCurrentMediaItem] = useState({
    messageId: '',
    aspectRatio: '',
    contentType: '',
    durationSeconds: 0,
    fileName: '',
    fileSizeBytes: 0,
    targetDisplay: { size: [0, 0], rotation: '' },
    url: '',
  });
  const [elapsedLoopTimer, setElapsedLoopTimer] = useState(1);
  const [elapsedItemTimer, setElapsedItemTimer] = useState(0);
  const [pauseMoment, setPauseMoment] = useState(0);
  const [videoPausePosition, setVideoPausePosition] = useState(-1);
  const [isPaused, setIsPaused] = useState(false);
  const [secondHover, setSecondHover] = useState(false);
  const [showSecondsHover, setShowSecondsHover] = useState(false);
  const [hoverMouseX, sethoverMouseX] = useState(0);
  const [playerResolutions, setPlayerResolutions] = useState([]);
  const [playerResolution, setPlayerResolution] = useState('');
  const [playerRotation, setPlayerRotation] = useState(0);
  const [playerRotations, setPlayerRotations] = useState([0, 90, -90, 180]);
  const [mediaItems, setMediaItems] = useState([]);

  const totalLoopTime = mediaItems.reduce((accumulator, mediaItem) => {
    return accumulator + toNumber(mediaItem.durationSeconds);
  }, 0) || 0;

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 5,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
    },
  };

  const cancelPlayerTimers = () => {
    clearInterval(currentItemTimeoutRef.current);
    clearTimeout(currentItemIntervalRef.current);
  };

  /**
   * When Video JS Player is ready
   */
  const handlePlayerReady = (player) => {
    playerRef.current = player;

    if (currentMediaItem.contentType.includes('video') && videoPausePosition !== -1) {
      playerRef.current.autoplay(false);
      playerRef.current.currentTime(videoPausePosition);
    }

    // You can handle player events here, for example:
    /* player.on('waiting', () => {
      VideoPlayer.log('player is waiting');
    });

    player.on('dispose', () => {
      VideoPlayer.log('player will dispose');
    }); */
  };

  const videoJsOptions = {
    autoplay: true,
    controls: false,
    responsive: false,
    fluid: false,
  };

  const getMinuteSeconds = (timeToConvert) => {
    return TimeUtil.convertSeconds(timeToConvert);
  };

  /**
   * Memo function to show display player and only rerender when the media item changes
   */
  const displayPlayer = useMemo(
    () => (
      <VideoPlayer
        options={{
          ...videoJsOptions,
          videoPausePosition,
          isPaused,
          sources: [{
            src: currentMediaItem.url,
            type: 'video/mp4',
          }],
        }}
        onReady={handlePlayerReady}
      />
    ),
    [currentMediaItem],
  );

  /**
   * Memo function to display the loop timer
   */
  const elapsedLoopTime = useMemo(
    () => (
      <div className="loop-time-remaining">{getMinuteSeconds(elapsedLoopTimer)}</div>
    ),
    [elapsedLoopTimer],
  );

  /**
   * Memo function to display the item timer
   */
  const elapsedItemTime = useMemo(
    () => (
      <div className="loop-time-remaining">
        {getMinuteSeconds(elapsedItemTimer)}
      </div>
    ),
    [elapsedItemTimer],
  );

  /**
   * When clicking the progress bar, seeks out media item associated with that second in the progress bar
   */
  const seekCurrentMediaItem = (data, isHover) => {
    if (data <= mediaItems[0].durationSeconds && isLoop) --data;
    if (data === 0) ++data;
    let mediaSecondsTotal = 0;
    let currentMedia = {};
    for (let i = 0; i < mediaItems.length; i++) {
      if (data >= mediaSecondsTotal && data < (mediaSecondsTotal + mediaItems[i].durationSeconds)) {
        currentMedia = mediaItems[i];
        break;
      }
      mediaSecondsTotal += mediaItems[i].durationSeconds;
    }
    if (mediaSecondsTotal === totalLoopTime) {
      currentMedia = mediaItems[mediaItems.length - 1];
    }

    if (!isHover) {
      if (isLoop) {
        setElapsedItemTimer(0);
      } else {
        setElapsedItemTimer(data);
      }

      setCurrentMediaItem(JSON.parse(JSON.stringify(currentMedia)));
    } else {
      // setHoverImageSrc(currentMedia.url);
      // setHoverImageType(currentMedia.contentType);
    }
  };

  /**
   * Get the time within the current media item, relative to the overall loop
   */
  const getMediaItemRelativeTime = (pauseTime) => {
    let mediaSecondsTotal = 0;
    for (let i = 0; i < mediaItems.length; i++) {
      if (pauseTime >= mediaSecondsTotal && pauseTime < (mediaSecondsTotal + mediaItems[i].durationSeconds)) {
        break;
      }
      mediaSecondsTotal += mediaItems[i].durationSeconds;
    }

    return (pauseTime - mediaSecondsTotal);
  };

  /**
   * Get media items that match the selected resolution/rotation
   */
  const getMediaItems = ({
    resolution,
    rotation,
  }) => {
    rotation = toNumber(rotation);

    const mediaItems = sortBy(messages, ['sequence']).map(message => {
      return message.media.filter(item => {
        item.messageId = message._id;
        item.name = message.name;
        item.durationSeconds = Math.round(item.durationSeconds);

        if ((resolution === item?.targetDisplay?.size.join(','))
          && rotation === item?.targetDisplay?.rotation) {
          return true;
        }
        return false;
      });
    });

    const finalMediaItems = [];
    mediaItems.forEach(mediaitem => {
      if (mediaitem.length > 0) {
        finalMediaItems.push(mediaitem[0]);
      }
    });

    return finalMediaItems;
  };

  /**
   * Clicking anywhere on the progress bar will select the media item associated with that point
   * in time and load it in the Display Player
   */
  const onClickProgressBar = (e, data) => {
    if (isLoop) {
      if (data === 0) ++data;
      clearInterval(currentItemTimeoutRef.current);
      clearTimeout(currentItemIntervalRef.current);
      setElapsedLoopTimer(data);
      seekCurrentMediaItem(data, false);
      setPauseMoment(data);
      setVideoPausePosition(getMediaItemRelativeTime(data));
      elapsedTimeRef.current = data;
      setIsPaused(true);
    } else {
      clearInterval(currentItemTimeoutRef.current);
      clearTimeout(currentItemIntervalRef.current);
      setElapsedItemTimer(data);
      seekCurrentMediaItem(data, false);
      setPauseMoment(data);
      setVideoPausePosition(data);
      elapsedTimeRef.current = data;
      setIsPaused(true);
    }
  };

  const onMouseEnterProgressBar = () => {
    setShowSecondsHover(true);
  };

  const onMouseLeaveProgressBar = () => {
    setShowSecondsHover(false);
  };

  const onMouseMoveProgressBar = (e) => {
    setShowSecondsHover(true);
    const secondSize = progressBarRef.current.offsetWidth / totalLoopTime;
    let secondBeingHovered = 0;
    for (let i = 0; totalLoopTime > i; i++) {
      if ((e.pageX > ((i * secondSize) + (rectTracXRef.current)))
        && (e.pageX + ((i * secondSize) + (rectTracXRef.current) + secondSize))) {
        secondBeingHovered = i + 1;
      }
    }

    setSecondHover(secondBeingHovered);
    seekCurrentMediaItem(secondBeingHovered, true);
    sethoverMouseX(e.pageX - rectTracXRef.current);
  };

  /**
   * Show progress bar for the entire loop, rerender only when elapsedLoopTimer changes
   */
  const playerProgressBar = useMemo(
    () => (
      <div
        className="slider-wrapper"
        onMouseEnter={onMouseEnterProgressBar}
        onMouseLeave={onMouseLeaveProgressBar}
        onMouseMove={onMouseMoveProgressBar}
        ref={progressBarRef}
      >
        <Slider
          className={classNames('ProgressBar display-progress-bar')}
          classes={{
            rail: 'slider-rail',
            track: 'slider-track',
            thumb: 'slider-thumb',
          }}
          value={elapsedLoopTimer}
          min={0}
          max={totalLoopTime}
          onChange={onClickProgressBar}
          aria-label="Display Progress Bar"
        />
      </div>
    ),
    [elapsedLoopTimer],
  );

  useEffect(() => {
    if (!isEmpty(mediaItems) && !isEmpty(playerRotation) && !isEmpty(playerResolution)) {
      rectTracXRef.current = progressBarRef.current.getBoundingClientRect().x;

      const handleResize = () => {
        if (progressBarRef.current !== null) {
          rectTracXRef.current = progressBarRef.current.getBoundingClientRect().x;
        }
      };

      window.addEventListener('resize', handleResize);
    }
  }, [mediaItems]);

  /**
   * Show progress bar for a single media item, rerender only when elapsedItemTimer changes
   */
  const playerItemProgressBar = useMemo(
    () => (
      <div
        className="slider-wrapper"
        onMouseEnter={onMouseEnterProgressBar}
        onMouseLeave={onMouseLeaveProgressBar}
        onMouseMove={onMouseMoveProgressBar}
        ref={progressBarRef}
      >
        <Slider
          className={classNames('ProgressBar display-progress-bar')}
          classes={{
            rail: 'slider-rail',
            track: 'slider-track',
            thumb: 'slider-thumb',
          }}
          value={elapsedItemTimer}
          min={0}
          max={currentMediaItem.durationSeconds}
          onChange={onClickProgressBar}
          aria-label="custom thumb label"
        />
      </div>
    ),
    [elapsedItemTimer],
  );

  /**
   * Load the next item in the loop and set interval and timeout
   */
  const setNextTimer = (mediaItemIndex, wasPaused) => {
    // find relative time location within this media item
    let currentItemTime = wasPaused ? getMediaItemRelativeTime(pauseMoment - 1) : 0;

    if (!isLoop && mediaItemIndex === 1) {
      currentItemTime = 1;
    }
    /* eslint-disable-next-line max-len */
    const firstItemTime = wasPaused ? mediaItems[mediaItemIndex].durationSeconds - currentItemTime : mediaItems[mediaItemIndex].durationSeconds;
    let currentMediaItemIndex = mediaItemIndex;
    const currentItemTimeout = setTimeout(() => {
      // move to next item
      const currentItemInterval = setInterval(() => {
        currentItemTime++;
        setElapsedItemTimer(currentItemTime);
        const newTime = totalLoopTime === elapsedTimeRef.current ? elapsedTimeRef.current : ++elapsedTimeRef.current;
        setElapsedLoopTimer(newTime);
        if (currentItemTime >= mediaItems[mediaItemIndex].durationSeconds) {
          clearInterval(currentItemInterval);
          clearTimeout(currentItemTimeout);
          if (mediaItemIndex + 1 < mediaItems.length) {
            ++currentMediaItemIndex;
            setCurrentMediaItem(mediaItems[currentMediaItemIndex]);
            setNextTimer(currentMediaItemIndex);
          } else {
            console.log('Loop complete!');
          }
        }
      }, 1000);
      currentItemIntervalRef.current = currentItemInterval;
    }, firstItemTime);
    currentItemTimeoutRef.current = currentItemTimeout;
  };

  /**
   * Load the inital loop
   */
  const startLoopTimer = () => {
    const firstItemTime = mediaItems[0].durationSeconds;
    let currentItemTime = 0;
    let currentMediaItemIndex = 0;
    setCurrentMediaItem(mediaItems[0]);
    setElapsedLoopTimer(0);
    elapsedTimeRef.current = 0;
    const currentItemTimeout = setTimeout(() => {
      // move to next item
      const currentItemInterval = setInterval(() => {
        currentItemTime++;
        setElapsedItemTimer(currentItemTime);
        const newTime = ++elapsedTimeRef.current;
        setElapsedLoopTimer(newTime);
        if (isLoop && (currentItemTime >= mediaItems[0].durationSeconds + 1)) {
          clearInterval(currentItemInterval);
          clearTimeout(currentItemTimeout);
          ++currentMediaItemIndex;
          const nextMediaItem = mediaItems[currentMediaItemIndex];

          if (nextMediaItem) {
            setCurrentMediaItem(nextMediaItem);
            setNextTimer(currentMediaItemIndex);
          }
        }

        if (!isLoop && (currentItemTime >= mediaItems[0].durationSeconds + 1)) {
          clearInterval(currentItemInterval);
          clearTimeout(currentItemTimeout);
          ++currentMediaItemIndex;
          const nextMediaItem = mediaItems[currentMediaItemIndex];

          if (nextMediaItem) {
            setCurrentMediaItem(nextMediaItem);
            setNextTimer(currentMediaItemIndex);
            setElapsedItemTimer(1);
          }
        }
      }, 1000);
      currentItemIntervalRef.current = currentItemInterval;
    }, firstItemTime);
    currentItemTimeoutRef.current = currentItemTimeout;
  };

  /**
   * Restart loop player from current pause point
   */
  const startLoopPlayer = () => {
    // check if paused
    // if paused, get currentMediaItem and set player to start playing at paused moment
    // else start player from beginning

    let currentMediaIndex = 0;
    mediaItems.forEach((mediaitem, i) => {
      if (mediaitem.messageId === currentMediaItem.messageId) {
        currentMediaIndex = i;
      }
    });
    // setElapsedLoopTimer(startTime);
    setNextTimer(currentMediaIndex, true);
  };

  const createMessageResolutions = () => {
    const allResolutions = [];
    const allRotations = [];

    messages.forEach(message => {
      message.media.forEach(mediaItem => {
        const targetDisplaySize = mediaItem?.targetDisplay?.size;
        allResolutions.push(targetDisplaySize);

        if (propsResolution === targetDisplaySize.join(',')) {
          allRotations.push(mediaItem.targetDisplay.rotation);
        }
      });
    });

    const uniqResolutions = [];
    const uniqRotations = uniq(allRotations);

    allResolutions.forEach(resolution => {
      let foundResolution = false;
      uniqResolutions.forEach(res => {
        if ((`${resolution[0]},${resolution[1]}` === res.join(','))) {
          foundResolution = true;
        }
      });
      if (!foundResolution) {
        uniqResolutions.push(resolution);
      }
    });

    setPlayerResolutions(uniqResolutions);
    setPlayerRotations(uniqRotations);

    if (propsResolution && isFinite(propsRotation)) {
      const mediaItems = getMediaItems({
        resolution: propsResolution,
        rotation: propsRotation,
      });

      if (mediaItems.length > 0) {
        setPlayerResolution(propsResolution);
        setPlayerRotation(propsRotation);
        setMediaItems(mediaItems);
      }
    }
  };

  useEffect(() => {
    if (playerRef.current && currentMediaItem.contentType.includes('video') && videoPausePosition !== -1) {
      playerRef.current.currentTime(videoPausePosition);
    }
  }, [currentMediaItem.contentType, videoPausePosition]);

  useEffect(() => {
    if (!isPaused && !isEmpty(mediaItems)) {
      startLoopTimer();
    }
  }, [mediaItems]);

  useEffect(() => {
    createMessageResolutions();
  }, [messages]);

  const clickPause = () => {
    setIsPaused(true);
    if (currentMediaItem.contentType.includes('video')) {
      playerRef.current.pause();
    }
    setPauseMoment(elapsedLoopTimer);
    clearInterval(currentItemIntervalRef.current);
    clearTimeout(currentItemTimeoutRef.current);
  };

  const clickPlay = () => {
    if (currentMediaItem.contentType.includes('video')) playerRef.current.play();
    setVideoPausePosition(-1);
    setIsPaused(false);
    clearInterval(currentItemIntervalRef.current);
    clearTimeout(currentItemTimeoutRef.current);
    startLoopPlayer();
  };

  const clickRestart = () => {
    startLoopTimer();
  };

  /**
   * Load media item based on item clicked in the carousel
   */
  const loadCarouselItem = (mediaItemId) => {
    if (currentMediaItem.messageId === mediaItemId) return;
    const currMediaItem = mediaItems.filter((mediaItem) => mediaItem.messageId === mediaItemId)[0] || {};
    let currentMediaIndex = 0;
    mediaItems.forEach((mediaitem, i) => {
      if (mediaitem.messageId === currMediaItem.messageId) {
        currentMediaIndex = i;
      }
    });
    let mediaSecondsTotal = 0;
    for (let i = 0; i < mediaItems.length; i++) {
      if (i < currentMediaIndex) {
        mediaSecondsTotal += mediaItems[i].durationSeconds;
      }
    }

    onClickProgressBar(null, mediaSecondsTotal + 1);
    // clickPlay();
  };

  /**
   * Download the media file from link in carousel item
   */
  const onClickDownload = (mediaItem) => {
    // eslint-disable-next-line max-len
    const fileName = `${mediaItem?.name ? kebabCase(mediaItem.name) : 'unknown'}_${mediaItem?.targetDisplay?.rotation ?? 'unknown'}_${mediaItem?.targetDisplay?.size?.join('x') ?? 'unknown'}`;
    saveAs(mediaItem.url, fileName);
  };

  /**
   * Get Next Item nav button
   */
  const getSelectNextNavItem = () => {
    let nextMediaItemIndex = 0;
    mediaItems.forEach((mediaItem, i) => {
      if (mediaItem.messageId === currentMediaItem.messageId) nextMediaItemIndex = i + 1;
    });

    const nextMediaItem = mediaItems[nextMediaItemIndex];

    const isLastItem = nextMediaItemIndex === mediaItems.length;

    return (
      !isLastItem ? (
        <div
          className="loop-right-button"
          onClick={() => loadCarouselItem(nextMediaItem.messageId)}
        >
          <span
            style={{
              position: 'relative',
              lineHeight: '32px',
              verticalAlign: 'top',
              marginRight: 8,
            }}
          >
            {nextMediaItem.fileName}
          </span>
          <VibeIcon
            icon={viChevronRight}
            color={color.primary}
            size={34}
          />
        </div>
      ) : <div> </div>
    );
  };

  /**
   * Get Items for the carousel
   */
  const getCarouselItems = () => {
    const carouselItems = mediaItems.map((mediaItem, i) => {
      if (mediaItem.contentType === 'image') {
        return (
          <div
            style={{
              background: color.primary,
            }}
            key={`${mediaItem.fileName}-${i}`}
          >
            <div
              style={{
                position: 'relative',
                width: 157,
                height: 93,
                borderRadius: 6,
                border: mediaItem.messageId === currentMediaItem.messageId ? `2px solid ${color.yellow}` : '0',
                cursor: 'pointer',
              }}
              onClick={() => loadCarouselItem(mediaItem.messageId)}
            >
              <VibeIcon
                style={{
                  position: 'absolute',
                  top: 23,
                  left: 55,
                  zIndex: 10,
                }}
                icon={viPlayCircleDisplay}
                color={color.white}
                size={24}
              />
              <img
                style={{
                  width: 157,
                  clipPath: 'polygon(0 0%, 100% 0%, 100% 93px, 0 93px)',
                }}
                src={mediaItem.url}
                alt=""
              />
            </div>
            <div
              className="media-item-download"
              onClick={() => onClickDownload(mediaItem)}
            >
              <div className="label">
                {mediaItem.fileName}
              </div>

              <VibeIcon
                style={{
                  marginLeft: 2,
                }}
                icon={viDownload}
                color={color.white}
                size={14}
              />
            </div>
          </div>
        );
      }

      if (mediaItem.contentType.includes('video')) {
        return (
          <div key={`${mediaItem.fileName}-${i}`}>
            <div
              style={{
                position: 'relative',
                height: 93,
                cursor: 'pointer',
                background: 'rgba(0, 0, 0, 0.25)',
                borderRadius: 6,
                border: mediaItem.url === currentMediaItem.url ? `1px solid ${color.yellow}` : '1px solid transparent',
              }}
              onClick={() => loadCarouselItem(mediaItem.messageId)}
            >
              <VibeIcon
                style={{
                  position: 'absolute',
                  top: 23,
                  left: `calc(50% - 24px)`,
                  zIndex: 10,
                }}
                icon={viPlayCircleDisplay}
                color={color.white}
                size={24}
              />
            </div>
            <div
              className="media-item-download"
              onClick={() => onClickDownload(mediaItem)}
            >
              <div className="label">
                {mediaItem.fileName}
              </div>

              <VibeIcon
                icon={viDownload}
                color={color.primary}
                size={16}
              />
            </div>
          </div>
        );
      }

      return <div key={`media-${i}`}>No Image</div>;
    });

    return carouselItems;
  };

  /**
   * Display media item in the Display Player, based on whether it is an image or video
   */
  const getCurrentMediaItem = () => {
    let mediaDisplayItem = <> </>;

    if (currentMediaItem.contentType === 'image') {
      mediaDisplayItem = (
        <img
          style={{
            height: 486,
          }}
          src={currentMediaItem.url}
          alt=""
        />
      );
    }

    if (currentMediaItem.contentType.includes('video')) {
      mediaDisplayItem = displayPlayer;
    }

    return mediaDisplayItem;
  };

  const closePlayer = () => {
    clearInterval(currentItemIntervalRef.current);
    clearTimeout(currentItemTimeoutRef.current);
    setDisplay({
      show: false,
      messages: [],
      isLoop: true,
      resolution: null,
      rotation: 0,
    });
  };

  const onChangeResolution = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    setPlayerResolution(value);
    cancelPlayerTimers();

    if (!isEmpty(value)) {
      // set player rotations
      const allRotations = [];

      messages.forEach(message => {
        message.media.forEach(item => {
          if (value === item?.targetDisplay?.size?.join(',')) {
            allRotations.push(item.targetDisplay.rotation);
          }
        });
      });

      const uniqRotations = uniq(allRotations);
      setPlayerRotations(uniqRotations);
    }

    setPlayerRotation('');
    setMediaItems([]);
    setCurrentMediaItem({
      aspectRatio: '',
      contentType: '',
      durationSeconds: 0,
      fileName: '',
      fileSizeBytes: 0,
      targetDisplay: { size: [0, 0], rotation: 0 },
      url: '',
    });
  };

  const onChangeRotation = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    setPlayerRotation(value);
    cancelPlayerTimers();

    if (!isEmpty(value)) {
      if (!isEmpty(playerResolution)) {
        const mediaItems = getMediaItems({
          resolution: playerResolution,
          rotation: value,
        });

        setElapsedItemTimer(1);
        setElapsedLoopTimer(1);
        setPauseMoment(1);
        elapsedTimeRef.current = 1;
        if (mediaItems.length > 0) {
          setCurrentMediaItem(mediaItems[0]);
        } else {
          setCurrentMediaItem({
            messageId: '',
            aspectRatio: '',
            contentType: '',
            durationSeconds: 0,
            fileName: '',
            fileSizeBytes: 0,
            targetDisplay: { size: [0, 0], rotation: 0 },
            url: '',
          });
        }

        setMediaItems(mediaItems);
      } else {
        setMediaItems([]);
        setCurrentMediaItem({
          aspectRatio: '',
          contentType: '',
          durationSeconds: 0,
          fileName: '',
          fileSizeBytes: 0,
          resolution: [0, 0],
          targetDisplay: { size: [0, 0], rotation: 0 },
          url: '',
        });
      }
    } else {
      setMediaItems([]);
      setCurrentMediaItem({
        aspectRatio: '',
        contentType: '',
        durationSeconds: 0,
        fileName: '',
        fileSizeBytes: 0,
        targetDisplay: { size: [0, 0], rotation: '' },
        url: '',
      });
    }
  };

  /**
   * Play/Pause/Restart button for the Display Player
   */
  const getPlayerControlButton = () => {
    let marginTop = isPaused ? 0 : 6;
    let marginLeft = isPaused ? 0 : 6;
    let icon = isPaused ? viPlayCircleOutline : viPause;
    let size = isPaused ? 24 : 12;
    let onClick = isPaused ? clickPlay : clickPause;

    if (elapsedLoopTimer === totalLoopTime) {
      marginTop = 0;
      marginLeft = 0;
      icon = viCached;
      size = 24;
      onClick = clickRestart;
    }

    return (
      <VibeIcon
        style={{
          marginRight: 16,
          marginTop,
          marginLeft,
        }}
        icon={icon}
        color={color.white}
        size={size}
        onClick={onClick}
      />
    );
  };

  const getPlayerResolutions = () => {
    const playerRes = [{
      label: 'Select',
      value: '',
    }];

    return [
      ...playerRes,
      ...playerResolutions.map(resolution => {
        return {
          label: `${resolution[0]}x${resolution[1]}`,
          value: resolution.join(','),
        };
      }),
    ];
  };

  const getPlayerRotations = () => {
    const playerRotationOptions = [{
      label: 'Select',
      value: '',
    }];

    playerRotations.forEach(rotation => {
      playerRotationOptions.push({
        label: rotationFullLabel(rotation),
        value: rotation,
      });
    });

    return playerRotationOptions;
  };

  const getElapsedTime = () => {
    if (!isEmpty(mediaItems)) {
      if (isLoop) {
        return elapsedLoopTime;
      }
      return elapsedItemTime;
    }
    return <> </>;
  };

  const getProgressBar = () => {
    if (!isEmpty(mediaItems)) {
      if (isLoop) {
        return playerProgressBar;
      }
      return playerItemProgressBar;
    }
    return <> </>;
  };

  const getEmptyMessage = () => {
    if (isEmpty(mediaItems)) {
      if (!isEmpty(playerRotation) && !isEmpty(playerResolution)) {
        return (
          <div className="selected-resolution-message">
            No media items match this resolution and rotation.
          </div>
        );
      }
      return (
        <div className="selected-resolution-message">
          Please select resolution & rotation to preview media.
        </div>
      );
    }
    return <> </>;
  };

  return (
    <div
      className="display-player"
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backdropFilter: 'blur(8px)',
          textAlign: 'center',
          color: color.primary,
          background: 'rgba(52, 64, 84, 0.70)',
        }}
      />
      <div
        className={classNames('display-player-content', { 'player-empty': mediaItems.length === 0 })}
      >
        <div className="display-player-settings">
          <div className="display-player-settings-title">
            Preview Settings
          </div>
          <div className="display-player-dropdowns">
            <div className="resolution-select">
              <Field
                type="select"
                label="Resolution"
                name="playerResolution"
                value={playerResolution}
                tabIndex={14}
                options={getPlayerResolutions()}
                onChange={onChangeResolution}
              />
            </div>
            <div className="rotation-select">
              <Field
                type="select"
                label="Rotation"
                name="playerRotation"
                value={playerRotation}
                tabIndex={14}
                options={getPlayerRotations()}
                disabled={isEmpty(playerResolution)}
                onChange={onChangeRotation}
              />
            </div>
          </div>
          <div className="display-player-close">
            <VibeIcon
              icon={viClose}
              color={color.primary}
              size={24}
              onClick={closePlayer}
            />
          </div>
        </div>
        <div className="display-player-header">
          <div className="display-player-title">
            {currentMediaItem.name}
          </div>
          <div className="display-player-filename">
            {currentMediaItem.fileName}
          </div>
        </div>
        {!isEmpty(mediaItems) ? (
          <>
            <canvas
              id="videoCanvas"
              style={{
                border: '1px solid #ddd',
                display: 'none',
              }}
            />
            <div className="display-player-media">
              {getCurrentMediaItem()}
              <div className="display-player-progress-bar">
                <div className="display-player-button">
                  {getPlayerControlButton()}
                </div>
                {getElapsedTime()}
                {showSecondsHover
                  && (
                  <>
                    <div
                      className="seconds-hovered"
                      style={{
                        left: hoverMouseX,
                      }}
                    >
                      <div
                        className="seconds-hovered-time"
                      >
                        {getMinuteSeconds(secondHover)}
                      </div>
                      <VibeIcon
                        style={{
                          display: 'block',
                          position: 'relative',
                          margin: '0 auto',
                          marginTop: -22,
                        }}
                        size={48}
                        icon={viLoopVideo}
                        color={color.white}
                        tooltip="Image"
                        tooltipProps={{
                          placement: 'top',
                        }}
                      />
                    </div>
                    <div
                      className="hover-dot"
                      style={{
                        left: hoverMouseX,
                      }}
                    />
                  </>
                  )}
                {getProgressBar()}
                <div className="loop-total-time">
                  {isLoop ? getMinuteSeconds(totalLoopTime) : getMinuteSeconds(currentMediaItem.durationSeconds)}
                </div>
              </div>
            </div>
            <div className="loop-media-navigation">
              {getSelectNextNavItem()}
            </div>
            <div className="loop-media-carousel">
              <Carousel
                responsive={responsive}
                itemClass="carousel-item-padding"
                customRightArrow={<CustomRightArrow />}
                customLeftArrow={<CustomLeftArrow />}
                additionalTransfrom={-8}
              >
                {getCarouselItems()}
              </Carousel>
            </div>
          </>
        ) : (
          getEmptyMessage()
        )}
      </div>
    </div>
  );
}

const mapDispatchToProps = {
  setDisplay,
};

export default connect(null, mapDispatchToProps)(DisplayPlayer);
